import { useState, useEffect } from "react";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, getQueryFn } from "@/lib/queryClient";
import { retryGooglePush } from "@/lib/retryGooglePush";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Checklist, ChecklistTemplate } from "@shared/schema";
import { Trash2, GripVertical, Plus, AlertCircle, Save, FolderOpen, X, Check, ChevronDown, ChevronUp, ChevronsDown, ChevronsUp, Share2, UserCog, Bot, CalendarPlus, CalendarOff, Pencil, History, Users } from "lucide-react";
import ShareDialog from "@/components/share-dialog";

/** Run-history rows for one template: started / completed stamps, newest first. */
function TemplateHistoryList({ templateId, onBack }: { templateId: string; onBack: () => void }) {
  const { data: runs, isLoading } = useQuery<Array<{
    id: string;
    checklistName: string;
    startedAt: string;
    completedAt: string | null;
  }>>({
    queryKey: ["/api/checklist-templates", templateId, "history"],
  });
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  return (
    <div className="space-y-2" data-testid={`history-runs-${templateId}`}>
      <Button type="button" variant="ghost" size="sm" className="h-7 px-2 -ml-2" onClick={onBack} data-testid="button-history-back">
        <ChevronUp className="h-3.5 w-3.5 mr-1 rotate-[-90deg]" />
        All templates
      </Button>
      {isLoading ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Loading history…</p>
      ) : !runs || runs.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No history yet — it starts recording the next time this template is used.
        </p>
      ) : (
        runs.map((run) => (
          <div key={run.id} className="rounded-md border px-3 py-2" data-testid={`history-run-${run.id}`}>
            <p className="text-sm font-medium truncate">{run.checklistName}</p>
            <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
              <span>Started: {fmt(run.startedAt)}</span>
              {run.completedAt ? (
                <span className="text-green-600 dark:text-green-400">Completed: {fmt(run.completedAt)}</span>
              ) : (
                <span className="text-amber-600 dark:text-amber-400">In progress</span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

interface ChecklistItem {
  text: string;
  checked: boolean;
  /** Server-stamped attribution for checked items */
  checkedBy?: string | null;
  checkedAt?: string | null;
  checkedByName?: string | null;
}

interface ShareInfo {
  userId: string;
  name: string | null;
  permission: string;
}

export type ChecklistWithMeta = Checklist & {
  isOwner: boolean;
  sharePermission: "owner" | "edit" | "view";
  ownerName: string | null;
  completedByName: string | null;
  sharedWith: ShareInfo[];
};

export type TemplateWithMeta = ChecklistTemplate & {
  isOwner: boolean;
  ownerName: string | null;
  sharedWith: ShareInfo[];
};

interface AssistantWithUser {
  id: string;
  userId: string;
  user: { id: string; firstName: string | null; lastName: string | null; email: string | null };
}

interface CategoryOption {
  id: string;
  name: string | null;
}

interface SortableChecklistProps {
  checklist: ChecklistWithMeta;
  index: number;
  onToggleItem: (checklistId: string, itemIndex: number) => void;
  onDeleteChecklist: (id: string) => void;
  onUpdateChecklist: (id: string, data: Partial<Checklist>) => void;
  onSaveAsTemplate: (checklist: Checklist) => void;
  onShare: (checklist: ChecklistWithMeta) => void;
  onFriendShare: (checklist: ChecklistWithMeta) => void;
  onSchedule: (checklist: ChecklistWithMeta) => void;
  categories: CategoryOption[];
  isHighlighted?: boolean;
}

const NO_CATEGORY = "__none__";

interface ChecklistOccurrence {
  id: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  isRecurring: boolean;
  isCompleted: boolean;
  completedAt: string | null;
}
const EMPTY_CHECKLISTS: ChecklistWithMeta[] = [];
const EMPTY_TEMPLATES: TemplateWithMeta[] = [];
const EMPTY_CATEGORIES: CategoryOption[] = [];

/**
 * Shared sharing dialog for a checklist or template: pick a human assistant
 * and/or toggle Manus (AI) access on the resource itself.
 */
function ChecklistShareDialog({
  open,
  onClose,
  kind,
  resource,
}: {
  open: boolean;
  onClose: () => void;
  kind: "checklist" | "template";
  resource: (ChecklistWithMeta | TemplateWithMeta) | null;
}) {
  const { toast } = useToast();
  const [selectedAssistantId, setSelectedAssistantId] = useState("");
  const [permission, setPermission] = useState<"view" | "edit">("edit");

  useEffect(() => {
    if (open) {
      setSelectedAssistantId("");
      setPermission("edit");
    }
  }, [open, resource?.id]);

  const { data: assistants = [] } = useQuery<AssistantWithUser[]>({
    queryKey: ["/api/assistants"],
    enabled: open,
  });

  const baseUrl = kind === "checklist" ? "/api/checklists" : "/api/checklist-templates";
  const listKey = kind === "checklist" ? ["/api/checklists"] : ["/api/checklist-templates"];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: listKey });

  const shareMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", `${baseUrl}/${resource!.id}/shares`, { sharedWithId: selectedAssistantId, permission }),
    onSuccess: () => {
      invalidate();
      setSelectedAssistantId("");
      toast({ title: "Shared", description: "The assistant can now see this in their checklists." });
    },
    onError: (e: any) => toast({ title: "Failed to share", description: e?.message ?? "Please try again.", variant: "destructive" }),
  });

  const unshareMutation = useMutation({
    mutationFn: (sharedWithId: string) => apiRequest("DELETE", `${baseUrl}/${resource!.id}/shares/${sharedWithId}`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Share removed" });
    },
    onError: (e: any) => toast({ title: "Failed to remove share", description: e?.message ?? "", variant: "destructive" }),
  });

  const manusMutation = useMutation({
    mutationFn: (sharedWithManus: boolean) => apiRequest("PATCH", `${baseUrl}/${resource!.id}`, { sharedWithManus }),
    onSuccess: () => invalidate(),
    onError: (e: any) => toast({ title: "Failed to update Manus access", description: e?.message ?? "", variant: "destructive" }),
  });

  if (!resource) return null;
  const sharedWith = resource.sharedWith ?? [];
  const availableAssistants = assistants.filter((a) => !sharedWith.some((s) => s.userId === a.userId));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col" data-testid="dialog-share-checklist">
        <DialogHeader>
          <DialogTitle>Share {kind === "checklist" ? "Checklist" : "Template"}</DialogTitle>
          <DialogDescription>
            Share "{resource.name}" with your assistants or Manus (AI assistant)
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 p-6">
          {/* Manus toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Bot className="w-5 h-5 text-primary" />
              <div>
                <div className="font-medium">Share with Manus</div>
                <div className="text-sm text-muted-foreground">
                  Manus can read this {kind}{kind === "checklist" ? " and check items off" : " and start daily checklists from it"}.
                </div>
              </div>
            </div>
            <Switch
              checked={!!(resource as any).sharedWithManus}
              onCheckedChange={(v) => manusMutation.mutate(v)}
              disabled={manusMutation.isPending}
              data-testid="switch-share-manus"
            />
          </div>

          {/* Human assistants */}
          {sharedWith.length > 0 && (
            <div className="space-y-2">
              <Label>Currently shared with assistants</Label>
              <div className="space-y-2">
                {sharedWith.map((s) => (
                  <div key={s.userId} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`share-row-${s.userId}`}>
                    <div className="flex items-center gap-2">
                      <UserCog className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{s.name ?? "Assistant"}</span>
                      <Badge variant={s.permission === "edit" ? "default" : "secondary"}>
                        {s.permission === "edit" ? "Can Edit" : "View Only"}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => unshareMutation.mutate(s.userId)}
                      disabled={unshareMutation.isPending}
                      data-testid={`button-unshare-${s.userId}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t p-6">
          <Button variant="outline" onClick={onClose} data-testid="button-close-share-dialog">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SortableItemProps {
  item: ChecklistItem;
  index: number;
  checklistId: string;
  onToggle: () => void;
  isEditMode?: boolean;
  onTextChange?: (text: string) => void;
  onDelete?: () => void;
  readOnly?: boolean;
}

function SortableChecklistItem({
  item,
  index,
  checklistId,
  onToggle,
  isEditMode = false,
  onTextChange,
  onDelete,
  readOnly = false
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${checklistId}-item-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isEditMode) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center gap-3 p-3 bg-muted/50 rounded"
        data-testid={`checklist-item-edit-${checklistId}-${index}`}
      >
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
          data-testid={`drag-handle-item-${checklistId}-${index}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="flex-shrink-0 w-8 text-center text-sm font-medium">
          {index + 1}.
        </div>
        <Input
          value={item.text}
          onChange={(e) => onTextChange?.(e.target.value)}
          className="flex-1"
          data-testid={`input-edit-item-${index}`}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-destructive"
          data-testid={`button-delete-item-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-2 rounded hover:bg-muted/50"
      data-testid={`checklist-item-${checklistId}-${index}`}
    >
      {!readOnly && (
        <div
          {...attributes}
          {...listeners}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
          data-testid={`drag-handle-item-${checklistId}-${index}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
      )}
      <div className="flex-shrink-0 w-6 text-center text-sm text-muted-foreground font-medium">
        {index + 1}.
      </div>
      <Checkbox
        checked={item.checked}
        onCheckedChange={readOnly ? undefined : onToggle}
        disabled={readOnly}
        data-testid={`checkbox-item-${checklistId}-${index}`}
      />
      <span
        className={`flex-1 ${item.checked ? 'line-through text-muted-foreground' : ''}`}
        data-testid={`text-item-${checklistId}-${index}`}
      >
        {item.text}
      </span>
      {item.checked && (item.checkedByName || item.checkedAt) && (
        <span
          className="flex-shrink-0 text-xs text-muted-foreground"
          title={`Checked${item.checkedByName ? ` by ${item.checkedByName}` : ""}${item.checkedAt ? ` on ${new Date(item.checkedAt).toLocaleString()}` : ""}`}
          data-testid={`text-item-checked-by-${checklistId}-${index}`}
        >
          ✓ {item.checkedByName ?? "someone"}
          {item.checkedAt ? ` · ${new Date(item.checkedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
        </span>
      )}
    </div>
  );
}

function SortableChecklistCard({
  checklist,
  index,
  onToggleItem,
  onDeleteChecklist,
  onUpdateChecklist,
  onSaveAsTemplate,
  onShare,
  onFriendShare,
  onSchedule,
  categories,
  isHighlighted
}: SortableChecklistProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: checklist.id });

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(checklist.name);
  const [editedItems, setEditedItems] = useState<ChecklistItem[]>(checklist.items as ChecklistItem[]);
  const [editedCategory, setEditedCategory] = useState<string>(checklist.category ?? NO_CATEGORY);
  const [newItemText, setNewItemText] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [confirmUnschedule, setConfirmUnschedule] = useState(false);
  const isOwner = checklist.isOwner !== false;
  const canEdit = isOwner || checklist.sharePermission === "edit";
  const { toast: unscheduleToast } = useToast();

  // Stop-recurring: only offered on template-based checklists the caller has
  // a recurring calendar schedule for (flag computed server-side).
  const unscheduleMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/checklists/${checklist.id}/schedule`);
      return res.json();
    },
    onSuccess: (data: { deleted: number }) => {
      setConfirmUnschedule(false);
      queryClient.invalidateQueries({ queryKey: ["/api/checklists"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      unscheduleToast({
        title: "Recurring schedule stopped",
        description: data.deleted > 0
          ? `Removed ${data.deleted} calendar ${data.deleted === 1 ? "entry" : "entries"} for "${checklist.name}".`
          : "No calendar entries were left to remove.",
      });
    },
    onError: () => {
      unscheduleToast({ title: "Couldn't stop the recurring schedule", variant: "destructive" });
    },
  });

  useEffect(() => {
    if (isEditing) {
      setEditedName(checklist.name);
      setEditedItems(checklist.items as ChecklistItem[]);
      setEditedCategory(checklist.category ?? NO_CATEGORY);
      setNewItemText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const items = checklist.items as ChecklistItem[];
  const completedCount = items.filter(item => item.checked).length;
  const progress = items.length > 0 ? (completedCount / items.length) * 100 : 0;

  const handleSaveEdit = () => {
    // expectedUpdatedAt: optimistic-concurrency precondition — the server
    // rejects (409) if someone else changed the checklist since we loaded it.
    const expectedUpdatedAt = (checklist as any).updatedAt;
    if (isOwner) {
      onUpdateChecklist(checklist.id, {
        name: editedName,
        items: editedItems,
        category: editedCategory === NO_CATEGORY ? null : editedCategory,
        expectedUpdatedAt,
      } as any);
    } else {
      // Shared editors may only update items
      onUpdateChecklist(checklist.id, { items: editedItems, expectedUpdatedAt } as any);
    }
    setIsEditing(false);
  };

  const handleAddItem = () => {
    if (newItemText.trim()) {
      const updatedItems = [...editedItems, { text: newItemText.trim(), checked: false }];
      setEditedItems(updatedItems);
      setNewItemText("");
    }
  };

  const handleDeleteItem = (itemIndex: number) => {
    const updatedItems = editedItems.filter((_, idx) => idx !== itemIndex);
    setEditedItems(updatedItems);
  };

  const itemSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = parseInt(active.id.toString().split('-item-')[1]);
      const newIndex = parseInt(over.id.toString().split('-item-')[1]);

      if (!isNaN(oldIndex) && !isNaN(newIndex)) {
        const newOrder = arrayMove(items, oldIndex, newIndex);
        onUpdateChecklist(checklist.id, { items: newOrder, expectedUpdatedAt: (checklist as any).updatedAt } as any);
      }
    }
  };

  const handleItemDragEndEdit = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = parseInt(active.id.toString().split('-item-')[1]);
      const newIndex = parseInt(over.id.toString().split('-item-')[1]);

      if (!isNaN(oldIndex) && !isNaN(newIndex)) {
        const newOrder = arrayMove(editedItems, oldIndex, newIndex);
        setEditedItems(newOrder);
      }
    }
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className="relative"
        data-testid={`checklist-card-${checklist.id}`}
      >
        <Card className={isHighlighted ? "ring-2 ring-primary transition-shadow" : undefined}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div
                  {...attributes}
                  {...listeners}
                  className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded mt-1"
                  data-testid={`drag-handle-${checklist.id}`}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <CardTitle
                    className={`text-lg ${canEdit ? "cursor-pointer hover:text-primary transition-colors" : ""}`}
                    data-testid={`text-checklist-name-${checklist.id}`}
                    onClick={() => {
                      if (!canEdit) return;
                      setEditedName(checklist.name);
                      setEditedItems(items);
                      setIsEditing(true);
                    }}
                  >
                    {checklist.name}
                  </CardTitle>
                  <CardDescription>
                    {completedCount} of {items.length} items completed ({Math.round(progress)}%)
                  </CardDescription>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {checklist.category && (
                      <Badge variant="secondary" data-testid={`badge-category-${checklist.id}`}>{checklist.category}</Badge>
                    )}
                    {checklist.instanceDate && (
                      <Badge variant="outline" data-testid={`badge-daily-${checklist.id}`}>
                        <CalendarPlus className="w-3 h-3 mr-1" />
                        {checklist.instanceDate}
                      </Badge>
                    )}
                    {!isOwner && (
                      <Badge variant="outline" data-testid={`badge-shared-by-${checklist.id}`}>
                        <UserCog className="w-3 h-3 mr-1" />
                        Shared by {checklist.ownerName ?? "owner"}
                      </Badge>
                    )}
                    {isOwner && (checklist as any).sharedWithManus && (
                      <Badge variant="outline" data-testid={`badge-manus-${checklist.id}`}>
                        <Bot className="w-3 h-3 mr-1" />
                        Manus
                      </Badge>
                    )}
                    {isOwner && checklist.sharedWith?.length > 0 && (
                      <Badge variant="outline" data-testid={`badge-shared-with-${checklist.id}`}>
                        <Share2 className="w-3 h-3 mr-1" />
                        {checklist.sharedWith.map((s) => s.name ?? "Assistant").join(", ")}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onUpdateChecklist(checklist.id, { isExpanded: !checklist.isExpanded })}
                    data-testid={`button-toggle-expand-${checklist.id}`}
                  >
                    {checklist.isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                )}
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditedName(checklist.name);
                      setEditedItems(items);
                      setIsEditing(true);
                    }}
                    data-testid={`button-edit-${checklist.id}`}
                  >
                    Edit
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHistory(v => !v)}
                  title="Completion history"
                  data-testid={`button-history-${checklist.id}`}
                >
                  <History className="w-4 h-4" />
                </Button>
                {(isOwner || canEdit) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSchedule(checklist)}
                    title="Schedule on calendar"
                    data-testid={`button-schedule-${checklist.id}`}
                  >
                    <CalendarPlus className="w-4 h-4" />
                  </Button>
                )}
                {(isOwner || canEdit) && !!checklist.templateId && (checklist as any).hasRecurringSchedule && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmUnschedule(true)}
                    title="Stop recurring schedule"
                    className="text-amber-600 hover:text-amber-700"
                    data-testid={`button-stop-recurring-${checklist.id}`}
                  >
                    <CalendarOff className="w-4 h-4" />
                  </Button>
                )}
                {isOwner && (
                  <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onFriendShare(checklist)}
                    data-testid={`button-share-friends-${checklist.id}`}
                    title="Share with Friends"
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onShare(checklist)}
                    data-testid={`button-share-${checklist.id}`}
                    title="AI & Team Access"
                  >
                    <Bot className="w-4 h-4" />
                  </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onSaveAsTemplate(checklist)}
                      data-testid={`button-save-template-${checklist.id}`}
                    >
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteChecklist(checklist.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`button-delete-checklist-${checklist.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          {showHistory && (
            <CardContent className="pt-0 pb-4 border-b border-border/50">
              <ChecklistHistory checklistId={checklist.id} />
            </CardContent>
          )}
          <Dialog open={confirmUnschedule} onOpenChange={setConfirmUnschedule}>
            <DialogContent data-testid={`dialog-stop-recurring-${checklist.id}`}>
              <DialogHeader>
                <DialogTitle>Stop recurring checklist?</DialogTitle>
                <DialogDescription>
                  "{checklist.name}" currently repeats
                  {(checklist as any).recurringFrequency ? ` ${(checklist as any).recurringFrequency}` : ""} and
                  will no longer repeat. All of its entries will be removed from your
                  calendar — including any copies on your connected Google calendar.
                  The checklist itself and its history are kept.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmUnschedule(false)}
                  data-testid={`button-cancel-stop-recurring-${checklist.id}`}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => unscheduleMutation.mutate()}
                  disabled={unscheduleMutation.isPending}
                  data-testid={`button-confirm-stop-recurring-${checklist.id}`}
                >
                  {unscheduleMutation.isPending ? "Stopping..." : "Stop recurring"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {checklist.isExpanded && (
            <CardContent>
              <DndContext
                sensors={itemSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleItemDragEnd}
              >
                <SortableContext
                  items={items.map((_, idx) => `${checklist.id}-item-${idx}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {items.map((item, idx) => (
                      <SortableChecklistItem
                        key={idx}
                        item={item}
                        index={idx}
                        checklistId={checklist.id}
                        onToggle={() => onToggleItem(checklist.id, idx)}
                        readOnly={!canEdit}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Edit Dialog - Full Page */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Checklist</DialogTitle>
            <DialogDescription>
              Update checklist name and items
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 p-6">
            {isOwner && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Checklist Name</label>
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Enter checklist name..."
                  data-testid="input-edit-checklist-name"
                />
              </div>
            )}

            {isOwner && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Category</label>
                <Select value={editedCategory} onValueChange={setEditedCategory}>
                  <SelectTrigger data-testid="select-edit-category">
                    <SelectValue placeholder="No category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CATEGORY}>No category</SelectItem>
                    <CategorySelectGroups categories={categories} />
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-4">
              <label className="text-sm font-medium">Items</label>

              <DndContext
                sensors={itemSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleItemDragEndEdit}
              >
                <SortableContext
                  items={editedItems.map((_, idx) => `${checklist.id}-item-${idx}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {editedItems.map((item, idx) => (
                      <SortableChecklistItem
                        key={idx}
                        item={item}
                        index={idx}
                        checklistId={checklist.id}
                        onToggle={() => {}}
                        isEditMode={true}
                        onTextChange={(text) => {
                          const updated = [...editedItems];
                          updated[idx] = { ...updated[idx], text };
                          setEditedItems(updated);
                        }}
                        onDelete={() => handleDeleteItem(idx)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <div className="flex gap-2 mt-4">
                <Input
                  value={newItemText}
                  onChange={(e) => setNewItemText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddItem();
                    }
                  }}
                  placeholder="Add new item..."
                  data-testid="input-new-item"
                />
                <Button onClick={handleAddItem} data-testid="button-add-item">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t p-6">
            <Button variant="outline" onClick={() => setIsEditing(false)} data-testid="button-cancel-edit">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} data-testid="button-save-edit">
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ChecklistsPage() {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const [localChecklists, setLocalChecklists] = useState<ChecklistWithMeta[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [loadTemplateDialogOpen, setLoadTemplateDialogOpen] = useState(false);
  // History dialog: null = closed; "pick" = template picker; otherwise the
  // selected template's id whose run history is displayed.
  const [historyView, setHistoryView] = useState<null | "pick" | string>(null);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [selectedChecklistForTemplate, setSelectedChecklistForTemplate] = useState<Checklist | null>(null);
  const [shareTarget, setShareTarget] = useState<{ kind: "checklist" | "template"; id: string } | null>(null);
  const [friendShareTarget, setFriendShareTarget] = useState<{ kind: "checklist" | "checklist_template"; item: any } | null>(null);
  const [renameTemplateTarget, setRenameTemplateTarget] = useState<TemplateWithMeta | null>(null);
  const [renameTemplateName, setRenameTemplateName] = useState("");
  const [deleteTemplateTarget, setDeleteTemplateTarget] = useState<TemplateWithMeta | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  // Schedule-on-calendar dialog state
  const [scheduleTarget, setScheduleTarget] = useState<ChecklistWithMeta | null>(null);
  const [scheduleDate, setScheduleDate] = useState<string>("");
  const [scheduleTime, setScheduleTime] = useState<string>("09:00");
  const [scheduleAllDay, setScheduleAllDay] = useState(false);
  const [scheduleCalendarId, setScheduleCalendarId] = useState<string>("none");
  const [scheduleRepeat, setScheduleRepeat] = useState<"none" | "daily" | "weekly">("none");
  // Set when scheduling succeeded locally but the Google Calendar push failed;
  // holds everything needed to retry the push for the already-created series.
  const [googlePushFailure, setGooglePushFailure] = useState<{
    checklistId: string;
    eventId: string;
    calendarId: string;
  } | null>(null);
  const [highlightedChecklistId, setHighlightedChecklistId] = useState<string | null>(null);

  // Form state
  const [newChecklistName, setNewChecklistName] = useState("");
  const [newChecklistItems, setNewChecklistItems] = useState<string[]>([""]);
  const [newChecklistCategory, setNewChecklistCategory] = useState<string>(NO_CATEGORY);
  const [templateName, setTemplateName] = useState("");
  const [templateCategory, setTemplateCategory] = useState<string>(NO_CATEGORY);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [instanceName, setInstanceName] = useState("");

  const isCompletedView = location === "/checklists/completed";

  // Fetch checklists (stable empty-array fallback: a `= []` default creates a
  // new ref every render, which loops the filter effect below — React #185)
  const { data: checklistsData, isLoading, error } = useQuery<ChecklistWithMeta[]>({
    queryKey: ["/api/checklists"],
  });
  const allChecklists = checklistsData ?? EMPTY_CHECKLISTS;

  // Fetch templates
  const { data: templatesData } = useQuery<TemplateWithMeta[]>({
    queryKey: ["/api/checklist-templates"],
  });
  const templates = templatesData ?? EMPTY_TEMPLATES;

  // Fetch categories for assignment/filtering
  const { data: categoriesData } = useQuery<CategoryOption[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });
  const categories = categoriesData ?? EMPTY_CATEGORIES;

  // Live share dialog resource (re-resolved so the dialog reflects fresh data)
  const shareResource = shareTarget
    ? shareTarget.kind === "checklist"
      ? allChecklists.find((c) => c.id === shareTarget.id) ?? null
      : templates.find((t) => t.id === shareTarget.id) ?? null
    : null;

  // Filter checklists based on tab + category
  useEffect(() => {
    const filtered = allChecklists.filter(c => {
      const items = c.items as ChecklistItem[];
      const allChecked = items.length > 0 && items.every(item => item.checked);
      if (isCompletedView ? !allChecked : allChecked) return false;
      if (categoryFilter === "all") return true;
      if (categoryFilter === NO_CATEGORY) return !c.category;
      return c.category === categoryFilter;
    });
    setLocalChecklists(filtered);
  }, [allChecklists, isCompletedView, categoryFilter]);

  // Connected calendars (for the optional Google push when scheduling)
  const { data: calendarConnections = [] } = useQuery<any[]>({
    queryKey: ["/api/calendar/connections"],
    enabled: scheduleTarget !== null,
  });
  const googleCalendars = calendarConnections
    .filter((c: any) => c.provider === "google")
    .flatMap((c: any) => c.externalCalendars ?? []);

  // Reset schedule form each time the dialog opens
  useEffect(() => {
    if (scheduleTarget) {
      setScheduleDate(new Date().toISOString().split("T")[0]);
      setScheduleTime("09:00");
      setScheduleAllDay(false);
      setScheduleCalendarId("none");
      setScheduleRepeat("none");
    }
    setGooglePushFailure(null);
  }, [scheduleTarget?.id]);

  // Deep link from a calendar event: /checklists?open=<id> highlights that checklist
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get("open");
    if (!openId || allChecklists.length === 0) return;
    setHighlightedChecklistId(openId);
    // Give the list a tick to render, then scroll to the card.
    setTimeout(() => {
      document.querySelector(`[data-testid="checklist-card-${openId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    const timer = setTimeout(() => setHighlightedChecklistId(null), 4000);
    return () => clearTimeout(timer);
  }, [allChecklists.length]);

  const scheduleMutation = useMutation({
    mutationFn: async () => {
      if (!scheduleTarget || !scheduleDate) throw new Error("Pick a date first");
      const startTime = scheduleAllDay
        ? new Date(`${scheduleDate}T00:00:00`).toISOString()
        : new Date(`${scheduleDate}T${scheduleTime || "09:00"}:00`).toISOString();
      const res = await apiRequest("POST", `/api/checklists/${scheduleTarget.id}/schedule`, {
        startTime,
        allDay: scheduleAllDay,
        targetCalendarId: scheduleCalendarId !== "none" ? scheduleCalendarId : undefined,
        repeat: scheduleRepeat,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      if (data?.googlePushFailed && scheduleTarget) {
        // Local event(s) were created, but the Google copy is missing.
        // Keep the dialog open and surface a one-tap retry.
        setGooglePushFailure({
          checklistId: scheduleTarget.id,
          eventId: data.id,
          calendarId: scheduleCalendarId,
        });
        toast({
          title: "Scheduled, but Google Calendar failed",
          description: "The checklist is on your calendar here, but we couldn't add it to Google Calendar.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Checklist scheduled", description: "It's now on your calendar." });
      setScheduleTarget(null);
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't schedule checklist",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  const retryGooglePushMutation = useMutation({
    mutationFn: async () => {
      if (!googlePushFailure) throw new Error("Nothing to retry");
      return retryGooglePush(googlePushFailure.checklistId, {
        eventId: googlePushFailure.eventId,
        targetCalendarId: googlePushFailure.calendarId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      toast({ title: "Added to Google Calendar", description: "The Google copy was created successfully." });
      setGooglePushFailure(null);
      setScheduleTarget(null);
    },
    onError: () => {
      toast({
        title: "Google Calendar push failed again",
        description: "You can try again, or close this dialog — the checklist is still on your calendar here.",
        variant: "destructive",
      });
    },
  });

  // Create checklist mutation
  const createMutation = useMutation({
    mutationFn: (data: { name: string; items: ChecklistItem[]; category?: string | null }) =>
      apiRequest("POST", "/api/checklists", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/checklists"] });
      setCreateDialogOpen(false);
      setNewChecklistName("");
      setNewChecklistItems([""]);
      setNewChecklistCategory(NO_CATEGORY);
    },
    onError: () => {
    },
  });

  // Update checklist mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Checklist> }) =>
      apiRequest("PATCH", `/api/checklists/${id}`, data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["/api/checklists"] });
    },
    onError: (e: any) => {
      const isConflict = typeof e?.message === "string" && e.message.includes("changed by someone else");
      toast({
        title: isConflict ? "Checklist changed elsewhere" : "Couldn't update checklist",
        description: isConflict
          ? "Someone (or your assistant) updated this checklist just now. It has been refreshed — please retry your change."
          : (e?.message ?? "Please try again."),
        variant: "destructive",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/checklists"] });
    },
  });

  // Delete checklist mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/checklists/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/checklists"] });
    },
    onError: () => {
    },
  });

  // Save template mutation
  const saveTemplateMutation = useMutation({
    mutationFn: (data: { name: string; items: string[]; category?: string | null }) =>
      apiRequest("POST", "/api/checklist-templates", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/checklist-templates"] });
      setSaveTemplateDialogOpen(false);
      setTemplateName("");
      setTemplateCategory(NO_CATEGORY);
      setSelectedChecklistForTemplate(null);
    },
    onError: () => {
    },
  });

  // Rename template mutation
  const renameTemplateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiRequest("PATCH", `/api/checklist-templates/${id}`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/checklist-templates"] });
      setRenameTemplateTarget(null);
      setRenameTemplateName("");
      toast({ title: "Template renamed" });
    },
    onError: (e: any) =>
      toast({ title: "Failed to rename template", description: e?.message ?? "Please try again.", variant: "destructive" }),
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/checklist-templates/${id}`),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/checklist-templates"] });
      setDeleteTemplateTarget(null);
      if (selectedTemplateId === id) {
        setSelectedTemplateId("");
        setInstanceName("");
      }
      toast({ title: "Template deleted" });
    },
    onError: (e: any) =>
      toast({ title: "Failed to delete template", description: e?.message ?? "Please try again.", variant: "destructive" }),
  });

  // Start today's checklist from a template
  const startDailyMutation = useMutation({
    mutationFn: (templateId: string) =>
      apiRequest("POST", `/api/checklist-templates/${templateId}/daily`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/checklists"] });
      setLoadTemplateDialogOpen(false);
      toast({ title: "Today's checklist started", description: "It's now in your Active tab." });
    },
    onError: (e: any) => {
      const msg = String(e?.message ?? "");
      toast({
        title: msg.includes("409") || msg.toLowerCase().includes("already") ? "Already started" : "Couldn't start checklist",
        description: msg.includes("409") || msg.toLowerCase().includes("already")
          ? "Today's checklist for this template already exists."
          : (msg || "Please try again."),
        variant: msg.includes("409") || msg.toLowerCase().includes("already") ? undefined : "destructive",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/checklists"] });
    },
  });

  // Load from template mutation
  const loadTemplateMutation = useMutation({
    mutationFn: ({ templateId, name }: { templateId: string; name: string }) =>
      apiRequest("POST", `/api/checklists/from-template/${templateId}`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/checklists"] });
      setLoadTemplateDialogOpen(false);
      setSelectedTemplateId("");
      setInstanceName("");
    },
    onError: () => {
    },
  });

  // Bulk expand/collapse mutation
  const bulkExpandMutation = useMutation({
    mutationFn: ({ isExpanded, isCompleted }: { isExpanded: boolean; isCompleted?: boolean }) =>
      apiRequest("PATCH", "/api/checklists/bulk/expand-state", { isExpanded, isCompleted }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/checklists"] });
    },
    onError: () => {
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localChecklists.findIndex((item) => item.id === active.id);
      const newIndex = localChecklists.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(localChecklists, oldIndex, newIndex);
        setLocalChecklists(newOrder);
        // TODO: Add backend endpoint for reordering if needed
      }
    }
  };

  const handleToggleItem = (checklistId: string, itemIndex: number) => {
    const checklist = allChecklists.find(c => c.id === checklistId);
    if (!checklist) return;

    const items = [...(checklist.items as ChecklistItem[])];
    items[itemIndex] = { ...items[itemIndex], checked: !items[itemIndex].checked };

    updateMutation.mutate({ id: checklistId, data: { items, expectedUpdatedAt: (checklist as any).updatedAt } as any });
  };

  const handleCreateChecklist = () => {
    if (!newChecklistName.trim()) {
      return;
    }

    const validItems = newChecklistItems.filter(item => item.trim());
    if (validItems.length === 0) {
      return;
    }

    const items: ChecklistItem[] = validItems.map(text => ({ text: text.trim(), checked: false }));
    createMutation.mutate({
      name: newChecklistName.trim(),
      items,
      category: newChecklistCategory === NO_CATEGORY ? null : newChecklistCategory,
    });
  };

  const handleSaveAsTemplate = (checklist: Checklist) => {
    setSelectedChecklistForTemplate(checklist);
    setTemplateName(checklist.name);
    setTemplateCategory(checklist.category ?? NO_CATEGORY);
    setSaveTemplateDialogOpen(true);
  };

  const handleSaveTemplate = () => {
    if (!templateName.trim() || !selectedChecklistForTemplate) {
      return;
    }

    const items = (selectedChecklistForTemplate.items as ChecklistItem[]).map(item => item.text);
    saveTemplateMutation.mutate({
      name: templateName.trim(),
      items,
      category: templateCategory === NO_CATEGORY ? null : templateCategory,
    });
  };

  const handleLoadTemplate = () => {
    if (!selectedTemplateId || !instanceName.trim()) {
      return;
    }

    loadTemplateMutation.mutate({ templateId: selectedTemplateId, name: instanceName.trim() });
  };

  const handleAddItemField = () => {
    setNewChecklistItems([...newChecklistItems, ""]);
  };

  const handleRemoveItemField = (index: number) => {
    if (newChecklistItems.length > 1) {
      setNewChecklistItems(newChecklistItems.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index: number, value: string) => {
    const updated = [...newChecklistItems];
    updated[index] = value;
    setNewChecklistItems(updated);
  };

  if (error) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load checklists. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full max-w-full p-4 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground" data-testid="text-page-title">Checklists</h1>
            <p className="text-muted-foreground mt-1">Manage your checklists and templates</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setHistoryView("pick")}
              variant="outline"
              data-testid="button-checklist-history"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
            <Button
              onClick={() => setLoadTemplateDialogOpen(true)}
              variant="outline"
              data-testid="button-load-template"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Load Template
            </Button>
            <Button
              onClick={() => setCreateDialogOpen(true)}
              data-testid="button-create-checklist"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Checklist
            </Button>
          </div>
        </div>

        <Tabs value={isCompletedView ? "completed" : "active"} onValueChange={(v) => {
          setLocation(v === "completed" ? "/checklists/completed" : "/checklists");
        }}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3 flex-wrap">
              <TabsList data-testid="tabs-checklist-view">
                <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
                <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
              </TabsList>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-44" data-testid="select-category-filter">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  <SelectItem value={NO_CATEGORY}>No category</SelectItem>
                  {categories.filter((c) => c.name).map((c) => (
                    <SelectItem key={c.id} value={c.name!}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {localChecklists.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bulkExpandMutation.mutate({
                    isExpanded: false,
                    isCompleted: isCompletedView ? true : undefined
                  })}
                  disabled={bulkExpandMutation.isPending}
                  data-testid="button-collapse-all"
                >
                  <ChevronsDown className="w-4 h-4 mr-2" />
                  Collapse All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => bulkExpandMutation.mutate({
                    isExpanded: true,
                    isCompleted: isCompletedView ? true : undefined
                  })}
                  disabled={bulkExpandMutation.isPending}
                  data-testid="button-expand-all"
                >
                  <ChevronsUp className="w-4 h-4 mr-2" />
                  Expand All
                </Button>
              </div>
            )}
          </div>

          <TabsContent value="active">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-48 w-full" />
                ))}
              </div>
            ) : localChecklists.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No active checklists</h3>
                    <p className="text-muted-foreground mb-4">Create a new checklist or load from a template to get started.</p>
                    <div className="flex gap-2 justify-center">
                      <Button onClick={() => setLoadTemplateDialogOpen(true)} variant="outline">
                        <FolderOpen className="w-4 h-4 mr-2" />
                        Load Template
                      </Button>
                      <Button onClick={() => setCreateDialogOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        New Checklist
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={localChecklists.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-4">
                    {localChecklists.map((checklist, index) => (
                      <SortableChecklistCard
                        key={checklist.id}
                        checklist={checklist}
                        index={index}
                        onToggleItem={handleToggleItem}
                        onDeleteChecklist={(id) => deleteMutation.mutate(id)}
                        onUpdateChecklist={(id, data) => updateMutation.mutate({ id, data })}
                        onSaveAsTemplate={handleSaveAsTemplate}
                        onShare={(c) => setShareTarget({ kind: "checklist", id: c.id })}
                        onFriendShare={(c) => setFriendShareTarget({ kind: "checklist", item: c })}
                        onSchedule={(c) => setScheduleTarget(c)}
                        categories={categories}
                        isHighlighted={highlightedChecklistId === checklist.id}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <Skeleton key={i} className="h-48 w-full" />
                ))}
              </div>
            ) : localChecklists.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-12">
                    <Check className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No completed checklists</h3>
                    <p className="text-muted-foreground">Completed checklists will appear here.</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {localChecklists.map((checklist, index) => (
                  <Card key={checklist.id} data-testid={`completed-checklist-${checklist.id}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{checklist.name}</CardTitle>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {checklist.category && <Badge variant="secondary">{checklist.category}</Badge>}
                            {checklist.instanceDate && (
                              <Badge variant="outline">
                                <CalendarPlus className="w-3 h-3 mr-1" />
                                {checklist.instanceDate}
                              </Badge>
                            )}
                            {!checklist.isOwner && (
                              <Badge variant="outline">
                                <UserCog className="w-3 h-3 mr-1" />
                                Shared by {checklist.ownerName ?? "owner"}
                              </Badge>
                            )}
                            {(checklist.completedByName || checklist.completedAt) && (
                              <Badge variant="outline" data-testid={`badge-completed-by-${checklist.id}`}>
                                <Check className="w-3 h-3 mr-1" />
                                {checklist.completedByName ? `Completed by ${checklist.completedByName}` : "Completed"}
                                {checklist.completedAt ? ` on ${new Date(checklist.completedAt).toLocaleDateString()}` : ""}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {checklist.isOwner && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMutation.mutate(checklist.id)}
                            className="text-destructive"
                            data-testid={`button-delete-completed-${checklist.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {(checklist.items as ChecklistItem[]).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2">
                            <div className="flex-shrink-0 w-6 text-center text-sm text-muted-foreground font-medium">
                              {idx + 1}.
                            </div>
                            <Checkbox checked disabled />
                            <span className="flex-1 line-through text-muted-foreground">{item.text}</span>
                            {item.checked && (item.checkedByName || item.checkedAt) && (
                              <span
                                className="flex-shrink-0 text-xs text-muted-foreground"
                                title={`Checked${item.checkedByName ? ` by ${item.checkedByName}` : ""}${item.checkedAt ? ` on ${new Date(item.checkedAt).toLocaleString()}` : ""}`}
                                data-testid={`text-item-checked-by-completed-${checklist.id}-${idx}`}
                              >
                                ✓ {item.checkedByName ?? "someone"}
                                {item.checkedAt ? ` · ${new Date(item.checkedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Checklist Dialog - Full Page */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col">
            <DialogHeader>
              <DialogTitle>Create New Checklist</DialogTitle>
              <DialogDescription>
                Add a name and items for your checklist
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-6 p-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Checklist Name</label>
                <Input
                  value={newChecklistName}
                  onChange={(e) => setNewChecklistName(e.target.value)}
                  placeholder="Enter checklist name..."
                  data-testid="input-new-checklist-name"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Category (optional)</label>
                <Select value={newChecklistCategory} onValueChange={setNewChecklistCategory}>
                  <SelectTrigger data-testid="select-new-checklist-category">
                    <SelectValue placeholder="No category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CATEGORY}>No category</SelectItem>
                    <CategorySelectGroups categories={categories} />
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <label className="text-sm font-medium">Items</label>
                <div className="space-y-2">
                  {newChecklistItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="flex-shrink-0 w-8 text-center text-sm font-medium">
                        {idx + 1}.
                      </div>
                      <Input
                        value={item}
                        onChange={(e) => handleItemChange(idx, e.target.value)}
                        placeholder="Enter item..."
                        data-testid={`input-checklist-item-${idx}`}
                      />
                      {newChecklistItems.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItemField(idx)}
                          className="text-destructive"
                          data-testid={`button-remove-item-${idx}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  onClick={handleAddItemField}
                  variant="outline"
                  className="w-full"
                  data-testid="button-add-item-field"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>
            </div>

            <DialogFooter className="border-t p-6">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)} data-testid="button-cancel-create">
                Cancel
              </Button>
              <Button onClick={handleCreateChecklist} disabled={createMutation.isPending} data-testid="button-submit-create">
                {createMutation.isPending ? "Creating..." : "Create Checklist"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save Template Dialog - Full Page */}
        <Dialog open={saveTemplateDialogOpen} onOpenChange={setSaveTemplateDialogOpen}>
          <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col">
            <DialogHeader>
              <DialogTitle>Save as Template</DialogTitle>
              <DialogDescription>
                Save this checklist as a reusable template
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-6 p-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Template Name</label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Enter template name..."
                  data-testid="input-template-name"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Category (optional)</label>
                <Select value={templateCategory} onValueChange={setTemplateCategory}>
                  <SelectTrigger data-testid="select-template-category">
                    <SelectValue placeholder="No category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CATEGORY}>No category</SelectItem>
                    <CategorySelectGroups categories={categories} />
                  </SelectContent>
                </Select>
              </div>

              {selectedChecklistForTemplate && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Items to Save</label>
                  <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                    {(selectedChecklistForTemplate.items as ChecklistItem[]).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="flex-shrink-0 w-8 text-center text-sm font-medium">
                          {idx + 1}.
                        </div>
                        <span>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="border-t p-6">
              <Button variant="outline" onClick={() => setSaveTemplateDialogOpen(false)} data-testid="button-cancel-save-template">
                Cancel
              </Button>
              <Button onClick={handleSaveTemplate} disabled={saveTemplateMutation.isPending} data-testid="button-submit-save-template">
                {saveTemplateMutation.isPending ? "Saving..." : "Save Template"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History Dialog: pick a template, then show its run history */}
        <Dialog open={historyView !== null} onOpenChange={(o) => { if (!o) setHistoryView(null); }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="dialog-checklist-history">
            <DialogHeader>
              <DialogTitle>
                {historyView === "pick"
                  ? "Checklist History"
                  : templates.find((t) => t.id === historyView)?.name ?? "History"}
              </DialogTitle>
              <DialogDescription>
                {historyView === "pick"
                  ? "Choose a template to see when its checklists were started and completed."
                  : "Each run shows when it was started and when it was completed."}
              </DialogDescription>
            </DialogHeader>
            {historyView === "pick" ? (
              <div className="space-y-1">
                {templates.length === 0 && (
                  <p className="text-sm text-muted-foreground py-4 text-center">No templates yet.</p>
                )}
                {templates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-md border hover:bg-muted"
                    onClick={() => setHistoryView(t.id)}
                    data-testid={`button-history-template-${t.id}`}
                  >
                    <History className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{t.name}</span>
                    {t.category && <Badge variant="secondary" className="shrink-0">{t.category}</Badge>}
                  </button>
                ))}
              </div>
            ) : historyView ? (
              <TemplateHistoryList templateId={historyView} onBack={() => setHistoryView("pick")} />
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Load Template Dialog - Full Page */}
        <Dialog open={loadTemplateDialogOpen} onOpenChange={setLoadTemplateDialogOpen}>
          <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col">
            <DialogHeader>
              <DialogTitle>Load from Template</DialogTitle>
              <DialogDescription>
                Create a new checklist from a saved template
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-6 p-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Template</label>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger data-testid="select-template">
                    <SelectValue placeholder="Choose a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id} data-testid={`template-option-${template.id}`}>
                        {template.name}
                        {template.category ? ` · ${template.category}` : ""}
                        {!template.isOwner ? ` (shared by ${template.ownerName ?? "owner"})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTemplateId && (() => {
                const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
                const templateItems = selectedTemplate?.items as string[] | undefined;

                return (
                  <>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => startDailyMutation.mutate(selectedTemplateId)}
                        disabled={startDailyMutation.isPending}
                        data-testid="button-start-today"
                      >
                        <CalendarPlus className="w-4 h-4 mr-2" />
                        {startDailyMutation.isPending ? "Starting..." : "Start Today's Checklist"}
                      </Button>
                      {selectedTemplate?.isOwner && (
                        <>
                          <Button
                            variant="outline"
                            onClick={() => setFriendShareTarget({ kind: "checklist_template", item: selectedTemplate })}
                            data-testid="button-share-template-friends"
                          >
                            <Share2 className="w-4 h-4 mr-2" />
                            Share Template
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setShareTarget({ kind: "template", id: selectedTemplateId })}
                            data-testid="button-share-template"
                          >
                            <Bot className="w-4 h-4 mr-2" />
                            AI Access
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setRenameTemplateTarget(selectedTemplate);
                              setRenameTemplateName(selectedTemplate.name);
                            }}
                            data-testid="button-rename-template"
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Rename Template
                          </Button>
                          <Button
                            variant="outline"
                            className="text-destructive"
                            onClick={() => setDeleteTemplateTarget(selectedTemplate)}
                            data-testid="button-delete-template"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Template
                          </Button>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      "Start Today's Checklist" creates a dated checklist for today from this template. Only one per day is allowed.
                    </p>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Checklist Name</label>
                      <Input
                        value={instanceName}
                        onChange={(e) => setInstanceName(e.target.value)}
                        placeholder="Enter name for this checklist..."
                        data-testid="input-instance-name"
                      />
                    </div>

                    {templateItems && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Template Items</label>
                        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                          {templateItems.map((item: string, idx: number) => (
                            <div key={idx} className="flex items-center gap-2">
                              <div className="flex-shrink-0 w-8 text-center text-sm font-medium">
                                {idx + 1}.
                              </div>
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <DialogFooter className="border-t p-6">
              <Button variant="outline" onClick={() => setLoadTemplateDialogOpen(false)} data-testid="button-cancel-load-template">
                Cancel
              </Button>
              <Button onClick={handleLoadTemplate} disabled={loadTemplateMutation.isPending} data-testid="button-submit-load-template">
                {loadTemplateMutation.isPending ? "Loading..." : "Create from Template"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rename Template Dialog */}
        <Dialog open={!!renameTemplateTarget} onOpenChange={(open) => { if (!open) setRenameTemplateTarget(null); }}>
          <DialogContent data-testid="dialog-rename-template">
            <DialogHeader>
              <DialogTitle>Rename Template</DialogTitle>
              <DialogDescription>
                Rename "{renameTemplateTarget?.name}". The new name will show everywhere this template appears.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={renameTemplateName}
                onChange={(e) => setRenameTemplateName(e.target.value)}
                placeholder="Enter template name..."
                data-testid="input-rename-template-name"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameTemplateTarget(null)} data-testid="button-cancel-rename-template">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (renameTemplateTarget && renameTemplateName.trim()) {
                    renameTemplateMutation.mutate({ id: renameTemplateTarget.id, name: renameTemplateName.trim() });
                  }
                }}
                disabled={!renameTemplateName.trim() || renameTemplateMutation.isPending}
                data-testid="button-submit-rename-template"
              >
                {renameTemplateMutation.isPending ? "Renaming..." : "Rename"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Template Dialog */}
        <Dialog open={!!deleteTemplateTarget} onOpenChange={(open) => { if (!open) setDeleteTemplateTarget(null); }}>
          <DialogContent data-testid="dialog-delete-template">
            <DialogHeader>
              <DialogTitle>Delete Template</DialogTitle>
              <DialogDescription>
                Delete "{deleteTemplateTarget?.name}"? It will be removed from the template picker for you and anyone it's shared with. Existing checklists created from it are not affected. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTemplateTarget(null)} data-testid="button-cancel-delete-template">
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (deleteTemplateTarget) {
                    deleteTemplateMutation.mutate(deleteTemplateTarget.id);
                  }
                }}
                disabled={deleteTemplateMutation.isPending}
                data-testid="button-confirm-delete-template"
              >
                {deleteTemplateMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Schedule-on-calendar Dialog */}
        <Dialog open={!!scheduleTarget} onOpenChange={(open) => { if (!open) setScheduleTarget(null); }}>
          <DialogContent data-testid="dialog-schedule-checklist">
            <DialogHeader>
              <DialogTitle>Schedule "{scheduleTarget?.name}"</DialogTitle>
              <DialogDescription>
                Put this checklist on your calendar. Clicking the event later brings you right back here.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  data-testid="input-schedule-date"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="schedule-all-day">All day</Label>
                <Switch
                  id="schedule-all-day"
                  checked={scheduleAllDay}
                  onCheckedChange={setScheduleAllDay}
                  data-testid="switch-schedule-all-day"
                />
              </div>
              {!scheduleAllDay && (
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    data-testid="input-schedule-time"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Repeat</Label>
                <Select value={scheduleRepeat} onValueChange={(v: "none" | "daily" | "weekly") => setScheduleRepeat(v)}>
                  <SelectTrigger data-testid="select-schedule-repeat">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Doesn't repeat</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
                {scheduleRepeat !== "none" && (
                  <p className="text-xs text-muted-foreground">
                    {scheduleRepeat === "daily"
                      ? "Adds a daily occurrence for the next ~3 months."
                      : "Adds a weekly occurrence (same weekday) for the next ~6 months."}
                  </p>
                )}
              </div>
              {googleCalendars.length > 0 && (
                <div className="space-y-2">
                  <Label>Also add to a Google Calendar (optional)</Label>
                  <Select value={scheduleCalendarId} onValueChange={setScheduleCalendarId}>
                    <SelectTrigger data-testid="select-schedule-calendar">
                      <SelectValue placeholder="Don't add to Google" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Don't add to Google</SelectItem>
                      {googleCalendars.map((cal: any) => (
                        <SelectItem key={cal.id} value={cal.id}>
                          {cal.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {googlePushFailure && (
                <div
                  className="rounded-md border border-destructive/50 bg-destructive/10 p-3 space-y-2"
                  data-testid="notice-google-push-failed"
                >
                  <p className="text-sm font-medium text-destructive">
                    Couldn't add to Google Calendar
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The checklist was scheduled on your calendar here, but the Google Calendar copy failed. You can retry without re-scheduling.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              {googlePushFailure ? (
                <>
                  <Button variant="outline" onClick={() => setScheduleTarget(null)} data-testid="button-dismiss-google-push-failed">
                    Close
                  </Button>
                  <Button
                    onClick={() => retryGooglePushMutation.mutate()}
                    disabled={retryGooglePushMutation.isPending}
                    data-testid="button-retry-google-push"
                  >
                    {retryGooglePushMutation.isPending ? "Retrying..." : "Retry Google Calendar"}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setScheduleTarget(null)}>Cancel</Button>
                  <Button
                    onClick={() => scheduleMutation.mutate()}
                    disabled={!scheduleDate || scheduleMutation.isPending}
                    data-testid="button-confirm-schedule"
                  >
                    {scheduleMutation.isPending ? "Scheduling..." : "Add to Calendar"}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share Dialog (checklists & templates) */}
        <ChecklistShareDialog
          open={!!shareTarget && !!shareResource}
          onClose={() => setShareTarget(null)}
          kind={shareTarget?.kind ?? "checklist"}
          resource={shareResource as any}
        />

        {friendShareTarget && (
          <ShareDialog
            isOpen={!!friendShareTarget}
            onClose={() => setFriendShareTarget(null)}
            itemId={friendShareTarget.item.id}
            itemType={friendShareTarget.kind}
            itemTitle={friendShareTarget.item.name}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Completion history for a scheduled checklist: each past calendar-event
 * occurrence is shown as a day tile with a completed/missed indicator.
 * Data comes from the checklist's linked calendar_events rows.
 */
export function ChecklistHistory({ checklistId }: { checklistId: string }) {
  const { data: occurrences, isLoading } = useQuery<ChecklistOccurrence[]>({
    queryKey: ["/api/checklists", checklistId, "occurrences"],
    // Explicit query function (the client-wide default would resolve the
    // same URL by joining the key, but keep the fetch explicit here).
    queryFn: getQueryFn<ChecklistOccurrence[]>({ on401: "throw" }),
  });

  if (isLoading) {
    return <Skeleton className="h-16 w-full" data-testid={`skeleton-history-${checklistId}`} />;
  }

  const all = occurrences ?? [];
  if (all.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`text-history-empty-${checklistId}`}>
        No scheduled occurrences yet. Use the calendar button to schedule this checklist.
      </p>
    );
  }

  // "Past" = the occurrence's day is before or equal to today (local time).
  // Days strictly before today count as missed when incomplete; today is
  // still in progress, so an incomplete today shows as pending instead.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTomorrow = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);

  const pastOrToday = all.filter(o => new Date(o.startTime) < startOfTomorrow);
  // Show the most recent 60 day-tiles, oldest first (server returns ascending).
  const visible = pastOrToday.slice(-60);
  const pastDone = pastOrToday.filter(o => o.isCompleted).length;

  if (pastOrToday.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`text-history-empty-${checklistId}`}>
        Scheduled, but no occurrences have come up yet.
      </p>
    );
  }

  const dayLabel = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const dayKey = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  return (
    <div className="space-y-2" data-testid={`history-${checklistId}`}>
      <p className="text-sm text-muted-foreground" data-testid={`text-history-summary-${checklistId}`}>
        Completed {pastDone} of {pastOrToday.length} {pastOrToday.length === 1 ? "day" : "days"}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((o) => {
          const isToday = new Date(o.startTime) >= startOfToday;
          const status = o.isCompleted ? "completed" : isToday ? "pending" : "missed";
          const colors =
            status === "completed"
              ? "bg-green-500/90 border-green-600 text-white"
              : status === "pending"
                ? "bg-muted border-border text-muted-foreground"
                : "bg-destructive/15 border-destructive/40 text-destructive";
          return (
            <div
              key={o.id}
              title={`${dayLabel(o.startTime)} — ${status === "completed" ? "Completed" : status === "pending" ? "In progress" : "Missed"}`}
              className={`flex h-8 w-8 items-center justify-center rounded border text-[10px] font-medium leading-none ${colors}`}
              data-testid={`history-day-${checklistId}-${dayKey(o.startTime)}`}
              data-status={status}
            >
              {status === "completed" ? <Check className="h-3.5 w-3.5" /> : status === "missed" ? <X className="h-3.5 w-3.5" /> : new Date(o.startTime).getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}
